<template>
  <div class="scan-progress-container" v-if="showProgress">
    <div class="scan-progress-header">
      <span class="scan-title">{{ getProgressTitle() }}</span>
      <span class="scan-percentage">{{ progressPercentage }}%</span>
    </div>
    <div class="progress-bar-container">
      <div class="progress-bar">
        <div class="progress-fill" :style="{ width: progressPercentage + '%' }"></div>
      </div>
    </div>
    <div class="scan-details" v-if="!batchFiles || batchFiles.length === 0">
      <span class="page-info" v-if="currentPage >= 0 && totalPages > 0">Processing pages {{ currentPage }} of {{ totalPages }}</span>
    </div>
    
    <!-- Individual File Progress for Batch Uploads -->
    <div v-if="taskType === 'batch-upload' && batchFiles && batchFiles.length > 0" class="batch-files-progress">
      <h5>Individual File Progress</h5>
      <div class="files-progress-list">
        <div 
          v-for="file in batchFiles" 
          :key="file.file_index"
          class="file-progress-item"
          :class="getFileStatusClass(file.status)"
        >
          <div class="file-progress-header">
            <span class="file-name">{{ file.file_name }}</span>
            <span class="file-status">{{ getStatusText(file.status) }}</span>
          </div>
          <div class="file-progress-bar-container" v-if="file.status === 'processing' || file.status === 'extracting'">
            <div class="file-progress-bar">
              <div class="file-progress-fill" :style="{ width: file.progress + '%' }"></div>
            </div>
            <div class="file-progress-details">
              <span v-if="file.progress">{{ file.progress }}%</span>
            </div>
          </div>
          <div class="file-progress-message" v-if="file.message">
            {{ file.message }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ScanProgressBar',
  props: {
    progressPercentage: {
      type: Number,
      default: 0
    },
    currentPage: {
      type: Number,
      default: 0
    },
    totalPages: {
      type: Number,
      default: 0
    },
    taskType: {
      type: String,
      default: 'upload'
    },
    showProgress: {
      type: Boolean,
      default: false
    },
    progressMessage: {
      type: String,
      default: ''
    },
    batchFiles: {
      type: Array,
      default: () => []
    }
  },
  methods: {
    getProgressTitle() {
      console.log(this.currentPage, this.totalPages);
      switch (this.taskType) {
        case 'upload':
          return 'Parsing File';
        case 'batch-upload':
          return 'Parsing Files';
        case 'processing':
          return 'Processing File';
        case 'scan':
          return 'Scanning File';
        case 'rescan':
          return 'Rescanning File';
        default:
          return 'Processing';
      }
    },
    
    getFileStatusClass(status) {
      switch (status) {
        case 'completed':
          return 'status-completed';
        case 'processing':
        case 'scanning':
          return 'status-processing';
        case 'extracting':
          return 'status-processing';
        case 'failed':
          return 'status-failed';
        case 'pending':
          return 'status-pending';
        default:
          return 'status-pending';
      }
    },
    
    getStatusText(status) {
      switch (status) {
        case 'completed':
          return 'Completed';
        case 'processing':
        case 'scanning':
          return 'Processing';
        case 'extracting':
          return 'Extracting';
        case 'failed':
          return 'Failed';
        case 'pending':
          return 'Pending';
        default:
          return 'Unknown';
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../index.scss';

.scan-progress-container {
  background: #ffffff;
  border-radius: 8px;
  padding: 1.5rem 2rem;
  margin: 1rem 0;
  border: 1px solid #eaeaea;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 100%;
}

.scan-progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.2rem;
  padding: 0 0.5rem;
}

.scan-title {
  color: #333333;
  font-weight: 500;
  font-size: 0.9rem;
  font-family: $brand-font-medium;
}

.scan-percentage {
  color: $color-primary;
  font-weight: bold;
  font-size: 0.9rem;
  font-family: $brand-font-bold;
}

.progress-bar-container {
  margin-bottom: 1.2rem;
  padding: 0 0.5rem;
}

.progress-bar {
  width: 100%;
  height: 12px;
  background: #f8f9fa;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #eaeaea;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, $color-primary 0%, adjust-color($color-primary, $lightness: -10%) 100%);
  border-radius: 6px;
  transition: width 0.3s ease;
  position: relative;
}

.progress-fill::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%);
  animation: shimmer 2s infinite;
}

@keyframes shimmer {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

.scan-details {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.8rem;
}

.page-info {
  color: #777777;
  font-family: $brand-font-regular;
  margin-left: 7px;
}

.task-type {
  color: $color-primary;
  text-transform: capitalize;
  font-weight: 500;
  font-family: $brand-font-medium;
}

// Batch Files Progress Styles
.batch-files-progress {
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #eaeaea;
  
  h5 {
    margin: 0 0 1rem 0;
    font-size: 0.9rem;
    color: #333333;
    font-weight: 600;
    font-family: $brand-font-medium;
  }
  
  .files-progress-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .file-progress-item {
    background: #f8f9fa;
    border: 1px solid #eaeaea;
    border-radius: 8px;
    padding: 1rem;
    transition: all 0.2s ease;
    
    &.status-completed {
      background: rgba(40, 167, 69, 0.1);
      border-color: #28a745;
    }
    
    &.status-processing {
      background: rgba($color-primary, 0.1);
      border-color: $color-primary;
    }
    
    &.status-failed {
      background: rgba(220, 53, 69, 0.1);
      border-color: #dc3545;
    }
    
    &.status-pending {
      background: #f8f9fa;
      border-color: #eaeaea;
    }
  }
  
  .file-progress-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.75rem;
    
    .file-name {
      font-size: 0.9rem;
      color: #333333;
      font-weight: 500;
      font-family: $brand-font-medium;
      flex: 1;
      margin-right: 1rem;
      word-break: break-word;
    }
    
    .file-status {
      font-size: 0.8rem;
      font-weight: 600;
      padding: 0.25rem 0.5rem;
      border-radius: 4px;
      text-transform: uppercase;
      font-family: $brand-font-medium;
      
      .status-completed & {
        background: #28a745;
        color: white;
      }
      
      .status-processing & {
        background: $color-primary;
        color: $color-secondary;
      }
      
      .status-failed & {
        background: #dc3545;
        color: white;
      }
      
      .status-pending & {
        background: #6c757d;
        color: white;
      }
    }
  }
  
  .file-progress-bar-container {
    margin-bottom: 0.5rem;
    
    .file-progress-bar {
      width: 100%;
      height: 8px;
      background: #e9ecef;
      border-radius: 4px;
      overflow: hidden;
      margin-bottom: 0.5rem;
      
      .file-progress-fill {
        height: 100%;
        background: linear-gradient(90deg, $color-primary 0%, adjust-color($color-primary, $lightness: -10%) 100%);
        border-radius: 4px;
        transition: width 0.3s ease;
      }
    }
    
    .file-progress-details {
      font-size: 0.8rem;
      color: #6c757d;
      font-family: $brand-font-regular;
    }
  }
  
  .file-progress-message {
    font-size: 0.8rem;
    color: #6c757d;
    font-style: italic;
    font-family: $brand-font-regular;
  }
}
</style> 